import React, { KeyboardEvent, useEffect, useState } from 'react'
import {
	SignInFormText,
	SignInFormGroup,
	SignInFormGroupInput,
	SignInFormGroupAction,
} from './SignIn.styled'
import { Text } from 'components/Text'
import { Input } from 'components/Input'
import { Button } from 'components/Button/Button'
import { useForm, Controller } from 'react-hook-form'
import { useAppDispatch, useAppState } from 'store'
import { authActions } from 'store/slices/auth'
import { LoginRequest } from 'services/auth/auth.types'
import { unwrapResult } from '@reduxjs/toolkit'
import toast from 'cogo-toast'
import { Helmet } from 'react-helmet'
import { baseTheme } from 'modules/Theme/styled'
import SignInTemplate from 'components/SignInTemplate'
import { translate } from 'i18n'
import { useHistory } from 'react-router'
import { scenes } from 'scenes/scenes'
import { PUBLIC_URL, SK_AUTH_URL } from '@constants/envs'
import PageLoader from 'modules/App/PageLoader'
import { useAppContext } from 'modules/App/AppContext'
import { API_URL } from '@constants'

const SignInPage = () => {
	const { isUnifiedDashboard } = useAppContext()

	switch (isUnifiedDashboard) {
		case true:
			return <UnifiedDashboardScreen />

		case false:
			return <LegacyScreen />

		default:
			return <PageLoader />
	}
}

function UnifiedDashboardScreen() {
	const history = useHistory()
	const dispatch = useAppDispatch()
	const { refreshFeatureFlags } = useAppContext()

	const deleteCookies = async () => {
		try {
			const response = await fetch(`${API_URL}/auth/logout`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`)
			}

			const responseData = await response.json()
		} catch (error) {
			console.error('Error making POST request:', error)
		}
	}

	useEffect(() => {
		const checkUser = async () => {
			dispatch(authActions.loginWithCookie())
				.then(unwrapResult)
				.then(resp => {
					const { user, onboardingStages } = resp
					if (resp) {
						dispatch(authActions.getMe())
							.then(unwrapResult)
							.then(async res => {
								if (res) {
									const isAdmin =
										user &&
										user.userRoles &&
										user.userRoles[0] &&
										user.userRoles[0].role &&
										user.userRoles[0].role.name === 'organization_owner'

									if (!!onboardingStages) {
										const userOrg = user?.organizations?.[0]
										if (isAdmin) {
											if (!userOrg) {
												if (!onboardingStages?.questionnaireStep) {
													history.replace(scenes.accountUsage.url)
												} else if (!onboardingStages?.organizationStep) {
													history.replace(scenes.organization.url)
												} else if (!onboardingStages?.userProfileStep) {
													history.replace(scenes.creatingProfile.url)
												} else if (
													!onboardingStages?.phoneNumberActivationStep
												) {
													history.replace(scenes.phone.url)
												} else if (!onboardingStages.is10dlcRegistered) {
													history.replace(scenes.setup.url)
												} else if (!onboardingStages.contactsAdded) {
													history.replace(scenes.setupCreateContact.url)
												} else if (!onboardingStages.isOptInFlowActive) {
													history.replace(scenes.setupOptinFlow.url)
												} else if (!onboardingStages.textsSent) {
													history.replace(scenes.setupFirstMessage.url)
												}
											} else if (userOrg && !user?.organizations?.[0]?.phone) {
												history.replace(scenes.phone.url)
											} else if (!onboardingStages.is10dlcRegistered) {
												history.replace(scenes.setup.url)
											} else if (!onboardingStages.contactsAdded) {
												history.replace(scenes.setupCreateContact.url)
											} else if (!onboardingStages.isOptInFlowActive) {
												history.replace(scenes.setupOptinFlow.url)
											} else if (!onboardingStages.textsSent) {
												history.replace(scenes.setupFirstMessage.url)
											} else {
												history.replace(scenes.mainHomeScreen.url)
											}
										} else {
											if (!onboardingStages?.questionnaireStep) {
												history.replace(scenes.accountUsage.url)
											} else if (!onboardingStages?.userProfileStep) {
												history.replace(scenes.creatingProfile.url)
											} else if (!onboardingStages.contactsAdded) {
												history.replace(scenes.setupCreateContact.url)
											} else if (!onboardingStages.isOptInFlowActive) {
												history.replace(scenes.setupOptinFlow.url)
											} else if (!onboardingStages.textsSent) {
												history.replace(scenes.setupFirstMessage.url)
											} else {
												history.replace(scenes.mainHomeScreen.url)
											}
										}
									}
								} else {
									deleteCookies()
									dispatch({ type: 'LOG_OUT' })
									await refreshFeatureFlags()
									window.location.replace(`${SK_AUTH_URL}/?convos`)
								}
							})
					}
				})
		}

		checkUser()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch])

	return <PageLoader />
}

function LegacyScreen() {
	const dispatch = useAppDispatch()
	const history = useHistory()
	const loading = useAppState(state => state.auth.loading)
	const { control, handleSubmit } = useForm()

	const onSubmit = handleSubmit((data: LoginRequest) => {
		dispatch(authActions.login(data))
			.then(unwrapResult)
			.then(res => {
				const { user, onboardingStages } = res
				const isAdmin =
					user &&
					user.userRoles &&
					user.userRoles[0] &&
					user.userRoles[0].role &&
					user.userRoles[0].role.name === 'organization_owner'

				if (!!onboardingStages) {
					const userOrg = user?.organizations?.[0]
					if (isAdmin) {
						if (!userOrg) {
							if (!onboardingStages?.questionnaireStep) {
								history.replace(scenes.accountUsage.url)
							} else if (!onboardingStages?.organizationStep) {
								history.replace(scenes.organization.url)
							} else if (!onboardingStages?.userProfileStep) {
								history.replace(scenes.creatingProfile.url)
							} else if (!onboardingStages?.phoneNumberActivationStep) {
								history.replace(scenes.phone.url)
							} else if (!onboardingStages.is10dlcRegistered) {
								history.replace(scenes.setup.url)
							} else if (!onboardingStages.contactsAdded) {
								history.replace(scenes.setupCreateContact.url)
							} else if (!onboardingStages.isOptInFlowActive) {
								history.replace(scenes.setupOptinFlow.url)
							} else if (!onboardingStages.textsSent) {
								history.replace(scenes.setupFirstMessage.url)
							}
						} else if (userOrg && !user?.organizations?.[0]?.phone) {
							history.replace(scenes.phone.url)
						} else if (!onboardingStages.is10dlcRegistered) {
							history.replace(scenes.setup.url)
						} else if (!onboardingStages.contactsAdded) {
							history.replace(scenes.setupCreateContact.url)
						} else if (!onboardingStages.isOptInFlowActive) {
							history.replace(scenes.setupOptinFlow.url)
						} else if (!onboardingStages.textsSent) {
							history.replace(scenes.setupFirstMessage.url)
						} else {
							history.replace(scenes.mainHomeScreen.url)
						}
					} else {
						if (!onboardingStages?.questionnaireStep) {
							history.replace(scenes.accountUsage.url)
						} else if (!onboardingStages?.userProfileStep) {
							history.replace(scenes.creatingProfile.url)
						} else if (!onboardingStages.contactsAdded) {
							history.replace(scenes.setupCreateContact.url)
						} else if (!onboardingStages.isOptInFlowActive) {
							history.replace(scenes.setupOptinFlow.url)
						} else if (!onboardingStages.textsSent) {
							history.replace(scenes.setupFirstMessage.url)
						} else {
							history.replace(scenes.mainHomeScreen.url)
						}
					}
				}
			})
			.catch(err => toast.error(err.message))
	})

	const handleGoToForgotPassword = () => history.push(scenes.forgotPassword.url)

	return (
		<SignInTemplate
			title={translate('login.signin')}
			subtitle={translate('login.subtitle')}
		>
			<Helmet>
				<meta charSet="utf-8" />
				<title>Sign in | Convos</title>
			</Helmet>

			<SignInFormGroup>
				<SignInFormGroupInput>
					<SignInFormText tx="general.emailAddress" weight="600" />
					<Controller
						placeholder="name@domain.com"
						style={{ height: 44 }}
						name="email"
						control={control}
						defaultValue=""
						as={Input}
					/>
				</SignInFormGroupInput>
				<SignInFormGroupInput>
					<SignInFormText tx="general.password" weight="600" />
					<Controller
						placeholder="Password"
						style={{ height: 44 }}
						name="password"
						defaultValue=""
						control={control}
						as={Input}
						type="password"
						onKeyUp={(evt: KeyboardEvent<HTMLInputElement>) =>
							evt.key === 'Enter' && onSubmit()
						}
					/>
				</SignInFormGroupInput>
			</SignInFormGroup>

			<SignInFormGroupAction>
				<Button
					preset={'accent'}
					tx="login.login"
					onClick={onSubmit}
					loading={loading}
				/>
				<Text
					tx="login.forgotPassword.question"
					weight="600"
					style={{ marginTop: '50px', cursor: 'pointer', fontSize: '14px' }}
					onClick={handleGoToForgotPassword}
				/>
			</SignInFormGroupAction>
		</SignInTemplate>
	)
}

export default SignInPage
